import { IOperatorTicketDetail, IPriceTier } from './types';
import { isWholeNetworkSelection } from './zones';

export interface IResolvedPrice {
	/** The matched price tier (carries `variantId` for the purchase), or null. */
	tier: IPriceTier | null;
	/** Convenience: `tier.priceInCents`, or null when unresolved. */
	priceInCents: number | null;
}

/**
 * Resolve the price tier for the current selection.
 *
 * Stop-based (§7b): flat lookup by price group — the single grid entry.
 * Zone-based (§8): a whole-network selection (see `isWholeNetworkSelection`,
 * e.g. two non-neighbouring zones) prices the `wholeNetwork` tier. Otherwise an
 * exact `numberOfZones` match within the price group, else fall through to the
 * `wholeNetwork` tier, else null (nothing selected or operator data
 * incomplete → CTA disabled).
 *
 * The price group is the one the user picked on the price-group selector
 * (`priceGroupId`); it falls back to the first group only when none was passed
 * (e.g. a deep link / refresh with no selection).
 */
export const resolvePriceTier = (
	ticketDetail: IOperatorTicketDetail | null,
	selectedZoneIds: string[],
	priceGroupId?: string
): IResolvedPrice => {
	if (!ticketDetail) return { tier: null, priceInCents: null };

	const priceGroup =
		ticketDetail.priceGroups.find((g) => g.id === priceGroupId) ??
		ticketDetail.priceGroups[0];
	if (!priceGroup) return { tier: null, priceInCents: null };

	const inGroup = ticketDetail.priceGrid.filter(
		(t) => t.priceGroupId === priceGroup.id
	);

	if (ticketDetail.pricingModel === 'stop-based') {
		const tier = inGroup[0] ?? null;
		return { tier, priceInCents: tier?.priceInCents ?? null };
	}

	if (selectedZoneIds.length === 0) return { tier: null, priceInCents: null };

	const wholeNetworkTier = inGroup.find((t) => t.wholeNetwork === true);
	const exactMatch = isWholeNetworkSelection(
		ticketDetail.zones,
		selectedZoneIds
	)
		? undefined
		: inGroup.find((t) => t.numberOfZones === selectedZoneIds.length);
	const tier = exactMatch ?? wholeNetworkTier ?? null;

	return { tier, priceInCents: tier?.priceInCents ?? null };
};
